import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, Send, Clock, User, Mail, Phone } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const LiveChatAdmin = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    fetchSessions();
    
    // Poll for new sessions every 5 seconds
    const interval = setInterval(fetchSessions, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedSession) {
      fetchMessages(selectedSession.session_id);
      startPolling(selectedSession.session_id);
    } else {
      stopPolling();
    }
    
    return () => stopPolling();
  }, [selectedSession]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const startPolling = (sessionId) => {
    stopPolling();
    pollingIntervalRef.current = setInterval(() => {
      fetchMessages(sessionId, true);
    }, 2000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/admin/live-chat/sessions`, {
        withCredentials: true
      });
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  };

  const fetchMessages = async (sessionId, silent = false) => {
    try {
      const response = await axios.get(`${API}/live-chat/messages/${sessionId}`, {
        withCredentials: true
      });
      
      // Only update if messages changed (avoid duplicates)
      const newMessageIds = response.data.map(m => m.message_id).sort().join(',');
      const currentMessageIds = messages.map(m => m.message_id).sort().join(',');
      
      if (newMessageIds !== currentMessageIds) {
        setMessages(response.data);
      }
      
      // Mark as read
      await axios.post(`${API}/admin/live-chat/mark-read/${sessionId}`, {}, {
        withCredentials: true
      });
      
      if (!silent) {
        // Refresh sessions to update unread count
        fetchSessions();
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedSession) return;
    
    const messageText = newMessage.trim();
    setNewMessage('');

    try {
      await axios.post(`${API}/admin/live-chat/send`, {
        session_id: selectedSession.session_id,
        message: messageText
      }, { withCredentials: true });
      
      // Refresh messages immediately
      fetchMessages(selectedSession.session_id, true);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Mesaj gönderilemedi');
    }
  };

  const totalUnread = sessions.reduce((sum, s) => sum + (s.unread_count || 0), 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(100vh-300px)]">
      {/* Sessions List */}
      <Card className="md:col-span-1">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span>Aktif Sohbetler</span>
            <Badge variant={totalUnread > 0 ? "destructive" : "secondary"}>
              {sessions.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 overflow-y-auto max-h-[calc(100vh-400px)]">
          {sessions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">Henüz aktif sohbet yok</p>
            </div>
          ) : (
            sessions.map((session) => (
              <button
                key={session.session_id}
                onClick={() => setSelectedSession(session)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  selectedSession?.session_id === session.session_id
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <h4 className="font-semibold text-sm">{session.customer_name}</h4>
                  {session.unread_count > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      {session.unread_count}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-600 truncate">{session.customer_email}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {format(new Date(session.last_activity), 'dd MMM HH:mm', { locale: tr })}
                </p>
              </button>
            ))
          )}
        </CardContent>
      </Card>

      {/* Chat Area */}
      <Card className="md:col-span-2 flex flex-col">
        {selectedSession ? (
          <>
            <CardHeader className="pb-3 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{selectedSession.customer_name}</CardTitle>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-600">
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {selectedSession.customer_email}
                    </span>
                    {selectedSession.customer_phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {selectedSession.customer_phone}
                      </span>
                    )}
                  </div>
                </div>
                <Badge variant="secondary">
                  <Clock className="w-3 h-3 mr-1" />
                  {format(new Date(selectedSession.started_at), 'dd MMM HH:mm', { locale: tr })}
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
                {messages.map((msg) => (
                  <div
                    key={msg.message_id}
                    className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-lg px-3 py-2 ${
                        msg.sender === 'admin'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white border border-slate-200 text-slate-900'
                      }`}
                    >
                      {msg.sender === 'admin' && msg.admin_name && (
                        <p className="text-xs opacity-75 mb-1">{msg.admin_name}</p>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <span className="text-xs opacity-70 mt-1 block">
                        {format(new Date(msg.timestamp), 'HH:mm', { locale: tr })}
                      </span>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSendMessage} className="p-3 bg-white border-t">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Mesajınızı yazın..."
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                  />
                  <Button 
                    type="submit" 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    disabled={!newMessage.trim()}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Enter ile gönder</p>
              </form>
            </CardContent>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-500">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p>Sohbet görüntülemek için soldan bir müşteri seçin</p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default LiveChatAdmin;
